import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { tasksApi } from '@/services';

interface Pagination {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
}

interface TaskCounts {
  notStarted: number;
  inProgress: number;
  cancelled: number;
  completed: number;
}

interface TaskState {
  tasks: any[];
  task: any | null;
  projects: any[];
  companies: any[];
  pagination: Pagination;
  counts: TaskCounts;
  loading: boolean;
  error: any | null;
}

const initialState: TaskState = {
  tasks: [],
  task: null,
  projects: [],
  companies: [],
  pagination: {
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  },
  counts: {
    notStarted: 0,
    inProgress: 0,
    cancelled: 0,
    completed: 0,
  },
  loading: false,
  error: null,
};

export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async (params: Record<string, any> = {}, { rejectWithValue }) => {
    try {
      const response = await tasksApi.getAll(params);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchTask = createAsyncThunk(
  'tasks/fetchTask',
  async (id: number | string, { rejectWithValue }) => {
    try {
      const response = await tasksApi.getById(id);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createTask = createAsyncThunk(
  'tasks/createTask',
  async (data: Record<string, any>, { rejectWithValue }) => {
    try {
      const response = await tasksApi.create(data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateTask = createAsyncThunk(
  'tasks/updateTask',
  async ({ id, data }: { id: number | string; data: Record<string, any> }, { rejectWithValue }) => {
    try {
      const response = await tasksApi.update(id, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteTask = createAsyncThunk(
  'tasks/deleteTask',
  async (id: number | string, { rejectWithValue }) => {
    try {
      await tasksApi.delete(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const taskSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<any>) {
      state.error = action.payload;
      state.loading = false;
    },
    clearError(state) {
      state.error = null;
    },
    clearTask(state) {
      state.task = null;
    },
    reset() {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchTasks
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload ?? {};
        const data = payload.data ?? {};
        state.tasks = data.tasks || [];
        state.projects = data.projects || [];
        state.companies = data.companies || [];
        const meta = payload.meta || {};
        state.pagination = {
          currentPage: meta.currentPage || 1,
          lastPage: meta.totalPages || meta.lastPage || 1,
          perPage: meta.perPage || 10,
          total: meta.total || meta.totalRows || state.tasks.length || 0,
        };
        state.counts = {
          notStarted: data.countNotStarted || 0,
          inProgress: data.countInProgress || 0,
          cancelled: data.countCancelled || 0,
          completed: data.countCompleted || 0,
        };
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchTask
      .addCase(fetchTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTask.fulfilled, (state, action) => {
        state.loading = false;
        state.task = action.payload;
      })
      .addCase(fetchTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // createTask
      .addCase(createTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.loading = false;
        // The service returns the sendResponse envelope; unwrap to the created
        // task (falling back to the raw payload for unwrapped responses) and
        // only insert real tasks so a wrapper like { data: null } never
        // corrupts the list.
        const created = action.payload?.data ?? action.payload;
        if (created && created.id != null) {
          state.tasks.unshift(created);
          // The row was prepended, so the header total must grow with it.
          state.pagination.total += 1;
          state.pagination.lastPage = Math.max(
            state.pagination.lastPage,
            Math.ceil(state.pagination.total / Math.max(1, state.pagination.perPage)),
          );
        }
      })
      .addCase(createTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // updateTask
      .addCase(updateTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tasks.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        if (state.task?.id === action.payload.id) {
          state.task = action.payload;
        }
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // deleteTask
      .addCase(deleteTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.loading = false;
        // Only decrement when a row actually disappeared: a second delete of the
        // same id (or a stale one) must not push the counter negative.
        const nextTasks = state.tasks.filter((t) => t.id !== action.payload);
        if (nextTasks.length !== state.tasks.length) {
          state.pagination.total = Math.max(0, state.pagination.total - 1);
          state.pagination.lastPage = Math.max(
            1,
            Math.ceil(state.pagination.total / Math.max(1, state.pagination.perPage)),
          );
        }
        state.tasks = nextTasks;
        if (state.task?.id === action.payload) {
          state.task = null;
        }
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setLoading, setError, clearError, clearTask, reset } = taskSlice.actions;

export default taskSlice.reducer;
